import { useCallback, useEffect, useState } from 'react'
import { generatePath } from 'react-router-dom'
import { Modal, useModals } from 'almanac-ui/Modal'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Button } from 'blaze-ui/Button'
import { BRAND_KITS_PATH } from 'paths'
import { ApiError } from 'utils/error'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import {
  openWeeklyPostLimitModal,
  WEEKLY_POST_LIMIT_ERROR_CODE,
} from 'components/WeeklyPostLimitReachedModal/openWeeklyPostLimitModal'
import { useAddToBrandPlanSchedule } from 'hooks/queries/schedules/useAddToBrandPlanSchedule'
import { usePreviewTopicsSync } from 'hooks/queries/schedules/usePreviewTopicsSync'
import { BrandPlan, CampaignOption } from 'types'
import { computePostTimes, getDefaultTacticsForPostType, sampleTopicForPostType } from '../CreateContentModal/utils'
import { CampaignChipDropdown } from './CampaignChipDropdown'
import { ComposeRow } from './ComposeRow'
import { AddMoreDropdown } from './AddMoreDropdown'
import { ComposeRowData } from './Types'
import { appendComposeRow, buildInitialComposeRow, expandComposeRowsToPayload } from './composeUtils'
import Styles from './NewPostPicker.module.scss'
import ComposeStyles from './ComposeRow.module.scss'

interface ComposeStepProps {
  brandPlan: BrandPlan
  initialSchedule: CampaignOption
  schedules: CampaignOption[]
  isLoadingSchedules: boolean
  initialIsBlank: boolean
  workspaceId: number
  onBack: () => void
  onPostsCreated: (postsCount: number, brandPlanScheduleId: number) => void
}

export const ComposeStep = ({
  brandPlan,
  initialSchedule,
  schedules,
  isLoadingSchedules,
  initialIsBlank,
  workspaceId,
  onBack,
  onPostsCreated,
}: ComposeStepProps) => {
  const { openModal, closeAllModals } = useModals()
  const [selectedScheduleId, setSelectedScheduleId] = useState(initialSchedule.id)
  const [composeRows, setComposeRows] = useState<ComposeRowData[]>(() => [
    buildInitialComposeRow(initialSchedule, initialIsBlank),
  ])

  const selectedSchedule = schedules.find((s) => s.id === selectedScheduleId) ?? initialSchedule

  const { mutate: addMoreContent, isLoading: isMutating } = useAddToBrandPlanSchedule()

  const { mutateAsync: generatePreviewTopics } = usePreviewTopicsSync({
    onSuccess: (preview, variables) => {
      if (!preview.topics) return
      let hasUnfilled = false
      setComposeRows((prev) => {
        const usedTopics = new Set(prev.filter((r) => !r.loading && r.topic).map((r) => r.topic))
        return prev.map((row) => {
          if (row.isBlank) return row
          if (!row.loading || row.userEdited) {
            return row.loading ? { ...row, loading: false } : row
          }
          const next = sampleTopicForPostType(preview.topics ?? {}, row.postType, usedTopics)
          if (next) {
            usedTopics.add(next)
            return { ...row, topic: next, loading: false }
          }
          hasUnfilled = true
          return row
        })
      })
      if (hasUnfilled && variables?.scheduleId) {
        setTimeout(() => triggerTopicGeneration(variables.scheduleId), 0)
      }
    },
    onError: () => {
      setComposeRows((prev) => prev.map((row) => (row.loading ? { ...row, loading: false } : row)))
    },
  })

  const triggerTopicGeneration = useCallback(
    (scheduleId: number) => {
      generatePreviewTopics({ scheduleId })
    },
    [generatePreviewTopics],
  )

  useEffect(() => {
    if (!initialIsBlank) triggerTopicGeneration(initialSchedule.id)
  }, [initialIsBlank, initialSchedule.id, triggerTopicGeneration])

  const handleScheduleChange = (id: number) => {
    setSelectedScheduleId(id)
    const newSchedule = schedules.find((s) => s.id === id)
    if (!newSchedule) return

    const postTimes = computePostTimes(newSchedule, composeRows.length)
    const hasAi = composeRows.some((r) => !r.isBlank)

    setComposeRows((prev) =>
      prev.map((row, i) => {
        const tactics = getDefaultTacticsForPostType(row.postType, newSchedule.tactics || [])
        const postAt = postTimes[i] ?? row.postAt
        if (row.isBlank) return { ...row, tactics, postAt }
        return {
          ...row,
          tactics,
          postAt,
          topic: '',
          loading: true,
          userEdited: false,
          referenceMedia: null,
        }
      }),
    )

    if (hasAi) triggerTopicGeneration(id)
  }

  const handleAddMore = (isBlank: boolean) => {
    setComposeRows((prev) => [...prev, appendComposeRow(selectedSchedule, prev, isBlank)])
    if (!isBlank) triggerTopicGeneration(selectedSchedule.id)
  }

  const updateRow = (id: string, updates: Partial<ComposeRowData>) => {
    setComposeRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...updates } : r)))
  }

  const removeRow = (id: string) => {
    setComposeRows((prev) => prev.filter((r) => r.id !== id))
  }

  const handleRegenerate = (id: string) => {
    setComposeRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, topic: '', loading: true, userEdited: false, referenceMedia: null } : r)),
    )
    triggerTopicGeneration(selectedSchedule.id)
  }

  const isAiLoading = composeRows.some((r) => !r.isBlank && r.loading)

  const handleSubmit = () => {
    addMoreContent(
      {
        schedule: expandComposeRowsToPayload(composeRows),
        brandPlanId: brandPlan.id,
        brandPlanScheduleId: selectedSchedule.id,
      },
      {
        onSuccess: () => {
          onPostsCreated(composeRows.length, selectedSchedule.id)
        },
        onError: (error) => {
          if (error instanceof ApiError && error.errorCode === 'no_brand_kit_media') {
            const brandKitPath = generatePath(BRAND_KITS_PATH, { workspaceId }) + '#images_videos'
            showToastMessage(
              'Your content preferences require brand kit media, but none are uploaded.',
              'error',
              { type: 'link', to: brandKitPath, label: 'Go to Brand Kit', variant: 'proceed' },
              10,
            )
          } else if (error instanceof ApiError && error.errorCode === WEEKLY_POST_LIMIT_ERROR_CODE) {
            openWeeklyPostLimitModal(openModal, closeAllModals)
          } else {
            showErrorMessageToast(error instanceof Error ? error.message : 'An error occurred while creating posts.')
          }
        },
      },
    )
  }

  return (
    <>
      <Modal.Header subHeader="Create one or more posts from scratch or with AI. AI generated posts will auto suggest a topic from your campaign and select a reference image.">
        <div className={Styles.headerRow}>
          <Heading level={2}>New Post</Heading>
          <CampaignChipDropdown
            schedules={schedules}
            selectedSchedule={selectedSchedule}
            isLoading={isLoadingSchedules}
            onSelect={handleScheduleChange}
          />
        </div>
      </Modal.Header>
      <Modal.Content>
        <div className={ComposeStyles.composeList}>
          {composeRows.map((row) => (
            <ComposeRow
              key={row.id}
              row={row}
              schedule={selectedSchedule}
              onUpdate={updateRow}
              onRemove={removeRow}
              onRegenerate={handleRegenerate}
              canRemove={composeRows.length > 1}
            />
          ))}
          <div className={ComposeStyles.addMoreRow}>
            <AddMoreDropdown onAdd={handleAddMore} />
          </div>
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="left">
          <Button variant="tertiary" size="lg" onPress={onBack}>
            Back
          </Button>
        </Modal.FooterContent>
        <Modal.FooterContent slot="right" className={Styles.footerRight}>
          <Button
            variant="primary"
            size="lg"
            isDisabled={isMutating || composeRows.length === 0 || isAiLoading}
            onPress={handleSubmit}
          >
            {isMutating ? 'Creating...' : `Schedule ${composeRows.length} Post${composeRows.length === 1 ? '' : 's'}`}
          </Button>
        </Modal.FooterContent>
      </Modal.Footer>
    </>
  )
}
